import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { clampPosition, viewport } from './placement';
import { useWindowStore } from './store';
import type { WindowState } from './types';

interface DragSession {
  pointerId: number;
  el: HTMLElement;
  /** Pointer position when the title bar was grabbed. */
  startX: number;
  startY: number;
  /** Window origin when the title bar was grabbed. */
  originX: number;
  originY: number;
  /** Width at grab time — `clampPosition` needs it and it can't change mid-drag. */
  width: number;
  prevUserSelect: string;
  detach: () => void;
}

export interface DragHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Title-bar dragging.
 *
 * Pointer capture keeps the stream coming even when the cursor outruns the
 * window or crosses an iframe, and every commit is throttled to one animation
 * frame so a fast drag can't queue up a hundred store writes.
 */
export function useDrag(win: WindowState, handleSelector?: string): DragHandlers {
  const winRef = useRef(win);
  const sessionRef = useRef<DragSession | null>(null);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<{ x: number; y: number } | null>(null);

  // Written after commit rather than during render, so a discarded render can
  // never leave the next pointerdown reading geometry that never shipped.
  useEffect(() => {
    winRef.current = win;
  });

  const flush = useCallback(() => {
    frameRef.current = null;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    useWindowStore.getState().move(winRef.current.id, pending.x, pending.y);
  }, []);

  const finish = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;

    session.detach();

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    // Land the last move that never got its frame.
    flush();

    try {
      if (session.el.hasPointerCapture(session.pointerId)) {
        session.el.releasePointerCapture(session.pointerId);
      }
    } catch {
      // The element can already be gone if the window closed mid-drag.
    }

    document.body.style.userSelect = session.prevUserSelect;
    useWindowStore.getState().setInteracting(false);
  }, [flush]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      if (sessionRef.current) return;

      // The caption buttons live inside the title bar; clicking them is never
      // the start of a drag.
      const target = event.target as Element | null;
      if (target?.closest('.title-bar-controls')) return;
      // A chromeless window hands these handlers its whole frame, so the grab
      // has to be narrowed to the strip the app nominated as its caption.
      if (handleSelector && !target?.closest(handleSelector)) return;

      const current = winRef.current;
      // A maximized window has nowhere to go.
      if (current.maximized) return;

      // No `preventDefault()` here: cancelling pointerdown suppresses the
      // compatibility mouse events, and double-click-to-maximize rides on
      // those. `touch-action` and `user-select` in the CSS already stop the
      // scrolling and text-selection this would otherwise guard against.
      const el = event.currentTarget;

      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        // Capture is an optimisation; the listeners below still work without it.
      }

      const handleMove = (e: PointerEvent) => {
        const session = sessionRef.current;
        if (!session || e.pointerId !== session.pointerId) return;
        pendingRef.current = clampPosition(
          session.originX + (e.clientX - session.startX),
          session.originY + (e.clientY - session.startY),
          session.width,
          viewport(),
        );
        if (frameRef.current === null) frameRef.current = requestAnimationFrame(flush);
      };

      const handleEnd = (e: PointerEvent) => {
        const session = sessionRef.current;
        if (session && e.pointerId !== session.pointerId) return;
        finish();
      };

      const detach = () => {
        el.removeEventListener('pointermove', handleMove);
        el.removeEventListener('pointerup', handleEnd);
        el.removeEventListener('pointercancel', handleEnd);
        el.removeEventListener('lostpointercapture', handleEnd);
      };

      // Capture retargets move/up to this element, so listening here is enough.
      el.addEventListener('pointermove', handleMove);
      el.addEventListener('pointerup', handleEnd);
      el.addEventListener('pointercancel', handleEnd);
      el.addEventListener('lostpointercapture', handleEnd);

      sessionRef.current = {
        pointerId: event.pointerId,
        el,
        startX: event.clientX,
        startY: event.clientY,
        originX: current.x,
        originY: current.y,
        width: current.w,
        prevUserSelect: document.body.style.userSelect,
        detach,
      };

      document.body.style.userSelect = 'none';
      useWindowStore.getState().setInteracting(true);
    },
    [finish, flush, handleSelector],
  );

  // Unmounting mid-drag (window closed from a menu, say) must still release
  // capture and clear the shield. Harmless when no drag is running, which is
  // what StrictMode's double mount exercises.
  useEffect(() => finish, [finish]);

  return { onPointerDown };
}
